import { useState } from "react";
import {
  Button,
  FormField,
  Modal,
  PeopleField,
  toast,
  type PeopleFieldValue,
} from "@playbookai/ui";
import { useAssignKpiOwner } from "../hooks/useAssignKpiOwner.js";
import { useKpiPeopleSource } from "../owner/use-kpi-people-source.js";
import { errorMessage } from "./error-message.js";

export interface AssignOwnerModalProps {
  readonly kpiId: string;
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * Assign / reassign a KPI's Owner (`PATCH /v1/kpis/:id/owner`). The owner is a
 * required precondition for activation; picked from the host's tenant
 * `PeopleSource`. A refused assignment is said under the field and keeps the
 * pick, so the admin can retry or choose someone else.
 */
export function AssignOwnerModal({
  kpiId,
  open,
  onClose,
}: AssignOwnerModalProps): JSX.Element {
  const source = useKpiPeopleSource();
  const assign = useAssignKpiOwner();
  const [owner, setOwner] = useState<PeopleFieldValue>(null);
  const [error, setError] = useState<string | null>(null);

  const ownerUserId = owner?.kind === "person" ? owner.id : null;
  const canSubmit = ownerUserId !== null && !assign.isPending;

  const handleClose = () => {
    if (assign.isPending) return;
    setOwner(null);
    setError(null);
    onClose();
  };

  const handleChange = (next: PeopleFieldValue) => {
    setOwner(next);
    setError(null);
  };

  const handleSubmit = () => {
    if (!ownerUserId) return;
    assign.mutate(
      { kpiId, input: { ownerUserId } },
      {
        onSuccess: () => {
          toast("Owner assigned", { tone: "healthy" });
          setOwner(null);
          onClose();
        },
        onError: (err) =>
          setError(errorMessage(err, "Could not assign the owner.")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="sm"
      title="Assign owner"
      description="The owner is the person accountable for managing and improving this KPI."
      footer={
        <>
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={assign.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={assign.isPending}
          >
            Assign owner
          </Button>
        </>
      }
    >
      <FormField label="Owner" required error={error}>
        <PeopleField
          value={owner}
          onChange={handleChange}
          source={source}
          label="Owner"
        />
      </FormField>
    </Modal>
  );
}
